/**
 * Integrytas Group — Theme designer (modal).
 *
 * Lets a user design a theme with live, whole-app preview: name, base mode,
 * font, density, font-size, and grouped color pickers for every semantic token.
 * Save creates/updates a personal theme (and applies it). Admins can additionally
 * share it as an org theme and/or set it as the app default. Cancel reverts the
 * live preview to the previously-active theme.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { usePresentation } from '../../lib/use_presentation';
import { TOKEN_GROUPS, FONT_STACKS, isValidHex } from '../../lib/theme/theme_tokens';
import type { ThemeDefinition, ThemeToken, Palette, ThemeMode, Density, FontSize } from '../../lib/theme/theme_tokens';

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24) || 'theme';

export function ThemeDesigner({ initial, mode, onClose }: { initial: ThemeDefinition; mode: 'new' | 'edit'; onClose: () => void }) {
  const { t } = useTranslation();
  const pres = usePresentation();

  const [name, setName]         = useState(mode === 'edit' ? initial.name : `${initial.name} (custom)`);
  const [themeMode, setThemeMode] = useState<ThemeMode>(initial.mode);
  const [font, setFont]         = useState(initial.font);
  const [density, setDensity]   = useState<Density>(initial.density);
  const [fontSize, setFontSize] = useState<FontSize>(initial.fontSize);
  const [colors, setColors]     = useState<Palette>({ ...initial.colors });
  const [shareOrg, setShareOrg] = useState(initial.scope === 'org');
  const [makeDefault, setMakeDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const working = useMemo<ThemeDefinition>(() => ({
    code: mode === 'edit' ? initial.code : `usr-${slug(name)}`,
    name: name.trim() || 'Untitled',
    scope: shareOrg ? 'org' : 'user',
    mode: themeMode,
    colors,
    font,
    density,
    fontSize,
    owner_user_id: initial.owner_user_id,
    theme_id: mode === 'edit' ? initial.theme_id : undefined,
  }), [mode, initial, name, shareOrg, themeMode, colors, font, density, fontSize]);

  // Live, whole-app preview while the designer is open; revert on unmount.
  useEffect(() => { pres.preview(working); return () => pres.preview(null); }, [working, pres]);

  const setColor = (token: ThemeToken, hex: string) => setColors((c) => ({ ...c, [token]: hex }));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!name.trim()) throw new Error(t('theme.err_name', 'A theme name is required.'));
      const code = mode === 'edit' ? initial.code : `usr-${slug(name)}-${Date.now().toString(36).slice(-4)}`;
      const def: ThemeDefinition = { ...working, code };
      pres.preview(null);
      if (pres.isAdmin && shareOrg) {
        await pres.publishOrgTheme(def);
        pres.setThemeCode(code);
      } else {
        await pres.saveUserTheme(def);
      }
      if (pres.isAdmin && makeDefault) await pres.setAppDefault(code);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => { pres.preview(null); onClose(); };

  const selectCls = 'h-8 w-full rounded border border-csm-border bg-csm-surface px-2 text-csm-sm text-csm-ink';

  return (
    <Modal
      title={mode === 'edit' ? t('theme.edit_title', 'Edit theme') : t('theme.new_title', 'Design a theme')}
      onClose={cancel}
      width="lg"
      footer={
        <>
          <button onClick={cancel} className="rounded border border-csm-border bg-csm-surface px-3 py-1 text-csm-sm text-csm-ink hover:bg-csm-surface-alt">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <p className="mb-3 text-csm-xs text-csm-muted">{t('theme.designer_hint', 'Changes preview live across the whole app. Save to keep this theme.')}</p>

      {/* Meta row */}
      <div className="mb-4 grid grid-cols-2 gap-x-4 gap-y-3">
        <label className="col-span-2 block">
          <span className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('theme.name_label', 'Theme name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="h-8 w-full rounded border border-csm-border bg-csm-surface px-2 text-csm-sm text-csm-ink" />
        </label>
        <label className="block">
          <span className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('theme.mode_label', 'Base mode')}</span>
          <select value={themeMode} onChange={(e) => setThemeMode(e.target.value as ThemeMode)} className={selectCls}>
            <option value="light">{t('theme.mode_light', 'Light')}</option>
            <option value="dark">{t('theme.mode_dark', 'Dark')}</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('theme.font_label', 'Font')}</span>
          <select value={font} onChange={(e) => setFont(e.target.value)} className={selectCls}>
            {FONT_STACKS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('presentation.density_label', 'Density')}</span>
          <select value={density} onChange={(e) => setDensity(e.target.value as Density)} className={selectCls}>
            <option value="compact">{t('presentation.density_compact', 'Compact')}</option>
            <option value="comfortable">{t('presentation.density_comfortable', 'Comfortable')}</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('presentation.font_size_label', 'Font size')}</span>
          <select value={fontSize} onChange={(e) => setFontSize(e.target.value as FontSize)} className={selectCls}>
            <option value="small">{t('presentation.font_small', 'Small')}</option>
            <option value="medium">{t('presentation.font_medium', 'Medium')}</option>
            <option value="large">{t('presentation.font_large', 'Large')}</option>
          </select>
        </label>
      </div>

      {/* Color groups */}
      <div className="space-y-4">
        {TOKEN_GROUPS.map((grp) => (
          <fieldset key={grp.group} className="rounded border border-csm-border-soft p-3">
            <legend className="px-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">{grp.group}</legend>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {grp.tokens.map(({ key, label }) => {
                const val = colors[key] ?? '#000000';
                return (
                  <div key={key} className="flex items-center gap-2">
                    <input type="color" value={isValidHex(val) ? val : '#000000'} onChange={(e) => setColor(key, e.target.value)} className="h-7 w-9 shrink-0 cursor-pointer rounded border border-csm-border bg-csm-surface p-0.5" aria-label={label} />
                    <input type="text" value={val} onChange={(e) => setColor(key, e.target.value)} className={`h-7 w-24 rounded border px-1.5 font-mono text-csm-xs text-csm-ink ${isValidHex(val) ? 'border-csm-border bg-csm-surface' : 'border-csm-danger bg-csm-danger-soft'}`} />
                    <span className="truncate text-csm-xs text-csm-ink-soft">{label}</span>
                  </div>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      {pres.isAdmin && (
        <div className="mt-4 rounded border border-csm-warning-border bg-csm-warning-soft px-3 py-2">
          <p className="mb-2 text-csm-xs font-semibold uppercase tracking-wider text-csm-warning">{t('theme.admin_section', 'Administrator')}</p>
          <label className="mb-1.5 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
            <input type="checkbox" checked={shareOrg} onChange={(e) => setShareOrg(e.target.checked)} className="h-4 w-4 accent-gray-700" />
            {t('theme.share_org', 'Share with everyone (organisation theme)')}
          </label>
          <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
            <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} className="h-4 w-4 accent-gray-700" />
            {t('theme.make_default', 'Make this the default theme for the app')}
          </label>
        </div>
      )}

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}
